"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { API_BASE_URL, API_UPLOAD_URL, getImageUrl } from '@/app/lib/api'; // adapte si nécessaire

type ApiCategory = {
  id: string;
  name: string;
  slug: string;
  image?: string | null;
  imageThum?: string | null;
  children?: ApiCategory[];
};

const AboveFooterSection = () => {
  const [sections, setSections] = useState<ApiCategory[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    async function loadParents() {
      try {
        setLoading(true);
        setError(null);
        const exclude = encodeURIComponent("meilleures-ventes,nos-coups-de-coeur");
        const resp = await fetch(`${API_BASE_URL}/categories/parents?exclude=${exclude}&expand=true`);
        if (!resp.ok) throw new Error(`Erreur API (${resp.status})`);
        const data = (await resp.json()) as ApiCategory[];
        if (active) {
          setSections(data.slice(0, 2));
        }
      } catch (err: unknown) {
        if (active) {
          setError(err instanceof Error ? err.message : "Erreur réseau");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }
    loadParents();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <section className="w-full py-10 px-4">
        <div className="max-w-7xl mx-auto">Chargement...</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="w-full py-10 px-4">
        <div className="max-w-7xl mx-auto text-red-600">Erreur: {error}</div>
      </section>
    );
  }

  // Si aucune catégorie récupérée, tu peux afficher un fallback statique ou rien
  if (!sections || sections.length === 0) {
    return (
      <section className="w-fullpy-10 px-4">
        <div className="max-w-7xl mx-auto">Aucune catégorie disponible.</div>
      </section>
    );
  }

  return (
    <section className="w-full py-10">
      <div className="max-w-6xl mx-auto flex md:grid md:grid-cols-2 overflow-x-auto md:overflow-visible snap-x snap-mandatory gap-6 px-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {sections.map((cat, index) => (
          <Link href={`/categorie/${cat.slug}`} key={cat.id} className="group shrink-0 snap-center w-[85vw] md:w-full">
            <motion.div
              className="relative overflow-hidden rounded-2xl cursor-pointer aspect-[16/10] md:aspect-square lg:aspect-[16/9] bg-gray-100"
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.3 }}
            >
              <div className="absolute inset-0">
                <Image
                  src={(cat.imageThum || cat.image) ? getImageUrl(cat.imageThum ?? cat.image) : "/placeholder.png"}
                  alt={cat.name}
                  fill
                  unoptimized
                  className="object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors duration-500" />
              </div>

              <div className="relative h-full w-full flex items-center justify-center px-8">
                <div className="absolute inset-0 flex items-center justify-center transition-all duration-500 ease-in-out opacity-100 group-hover:opacity-0 group-hover:translate-y-[-20px] z-20">
                  <p className="flex flex-col uppercase leading-none text-bold text-center">
                    <span className="text-lg md:text-4xl font-bold text-white">{cat.name}</span>
                    <span className="text-5xl md:text-7xl font-black text-white">{/* tu peux mettre un mot ici si besoin */}</span>
                    <span className="text-4xl md:text-6xl font-bold text-[#e41f82] drop-shadow-lg">
                      {/* Vide ou un sous-titre si tu veux */}
                    </span>
                  </p>
                </div>

                <div className="absolute inset-0 flex items-center justify-center transition-all duration-500 ease-in-out opacity-0 translate-y-[20px] group-hover:opacity-100 group-hover:translate-y-0 z-30">
                  <div className="relative w-3/4 h-3/4">
                    {/* Si tu veux, afficher l'image de la 1ère sous-catégorie (child) au hover : */}
                    {cat.children && cat.children.length > 0 ? (
                      <Image
                        src={cat.image ? getImageUrl(cat.image) : "/logo-tvss.png"}
                        alt={cat.name}
                        fill
                        unoptimized
                        className="object-contain drop-shadow-2xl"
                      />
                    ) : (
                      <Image
                        src={cat.image ? getImageUrl(cat.image) : "/logo-tvss.png"}
                        alt={cat.name}
                        fill
                        unoptimized
                        className="object-contain drop-shadow-2xl"
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="absolute inset-0 border-0 group-hover:bg-[#a81b8d]/80 transition-all duration-500 pointer-events-none rounded-2xl" />
            </motion.div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default AboveFooterSection;